/**
 * useCV Hook
 * Custom hook for managing CV/Resume operations
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  getUserCVs, 
  getUserPrimaryCV, 
  uploadCV, 
  deleteCV, 
  setPrimaryCV,
  updateCVMetadata 
} from '../services/cvService';
import { useAuth } from '../../../core/contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Custom hook for CV management
 * 
 * @returns {Object} CV data and management functions
 */
const useCV = () => {
  const { currentUser } = useAuth();
  
  // State management
  const [cvs, setCvs] = useState([]);
  const [primaryCV, setPrimaryCVState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Load user's CVs
  const loadCVs = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [userCVs, userPrimaryCV] = await Promise.all([
        getUserCVs(currentUser.uid),
        getUserPrimaryCV(currentUser.uid)
      ]);
      
      setCvs(userCVs);
      setPrimaryCVState(userPrimaryCV);
    } catch (err) {
      console.error('Error loading CVs:', err);
      setError(err.message || 'Failed to load CVs');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Upload new CV
  const handleUploadCV = useCallback(async (file, onProgress = null) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    try {
      setUploading(true);
      setError(null);
      
      const uploadedCV = await uploadCV(file, currentUser.uid, onProgress);
      
      // If this is the first CV, make it primary
      if (cvs.length === 0) {
        await setPrimaryCV(currentUser.uid, uploadedCV.id);
        setPrimaryCVState(uploadedCV);
      }
      
      // Add to CVs list
      setCvs(prev => [uploadedCV, ...prev]);
      
      toast.success('CV uploaded successfully!');
      return uploadedCV;
    } catch (err) {
      console.error('Error uploading CV:', err);
      setError(err.message || 'Failed to upload CV');
      toast.error(err.message || 'Failed to upload CV');
      throw err;
    } finally {
      setUploading(false);
    }
  }, [currentUser, cvs.length]);

  // Delete CV
  const handleDeleteCV = useCallback(async (cvId) => {
    if (!currentUser) return;
    
    try {
      setError(null);
      
      await deleteCV(cvId);
      
      // Remove from CVs list
      setCvs(prev => prev.filter(cv => cv.id !== cvId));
      
      // If deleted CV was primary, clear primary state
      if (primaryCV?.id === cvId) {
        setPrimaryCVState(null);
        
        // Set another CV as primary if available
        const remainingCVs = cvs.filter(cv => cv.id !== cvId);
        if (remainingCVs.length > 0) {
          await setPrimaryCV(currentUser.uid, remainingCVs[0].id);
          setPrimaryCVState(remainingCVs[0]);
        }
      }
      
      toast.success('CV deleted successfully');
    } catch (err) {
      console.error('Error deleting CV:', err);
      setError(err.message || 'Failed to delete CV');
      toast.error(err.message || 'Failed to delete CV');
      throw err;
    }
  }, [currentUser, primaryCV, cvs]);

  // Set primary CV
  const handleSetPrimaryCV = useCallback(async (cvId) => {
    if (!currentUser) return;
    
    try {
      setError(null);
      
      await setPrimaryCV(currentUser.uid, cvId);
      
      // Update primary CV state
      const newPrimaryCV = cvs.find(cv => cv.id === cvId);
      setPrimaryCVState(newPrimaryCV);
      
      // Update CVs list to reflect primary status
      setCvs(prev => prev.map(cv => ({
        ...cv,
        isPrimary: cv.id === cvId
      })));
      
      toast.success('Primary CV updated');
    } catch (err) {
      console.error('Error setting primary CV:', err);
      setError(err.message || 'Failed to set primary CV');
      toast.error(err.message || 'Failed to set primary CV');
      throw err;
    }
  }, [currentUser, cvs]);

  // Update CV metadata
  const handleUpdateCVMetadata = useCallback(async (cvId, metadata) => {
    if (!currentUser) return;
    
    try {
      setError(null);
      
      await updateCVMetadata(cvId, metadata);
      
      // Update CV in list
      setCvs(prev => prev.map(cv => 
        cv.id === cvId ? { ...cv, ...metadata } : cv
      ));
      
      // Update primary CV if it was updated
      if (primaryCV?.id === cvId) {
        setPrimaryCVState(prev => ({ ...prev, ...metadata }));
      }
      
      toast.success('CV updated successfully');
    } catch (err) {
      console.error('Error updating CV metadata:', err);
      setError(err.message || 'Failed to update CV');
      toast.error(err.message || 'Failed to update CV');
      throw err;
    }
  }, [currentUser, primaryCV]);

  // Get CV by ID
  const getCVById = useCallback((cvId) => {
    return cvs.find(cv => cv.id === cvId);
  }, [cvs]);

  // Check if user has any CVs
  const hasCVs = cvs.length > 0;

  // Check if CV is primary
  const isPrimary = useCallback((cvId) => {
    return primaryCV?.id === cvId;
  }, [primaryCV]);

  // Get CV statistics
  const getStats = useCallback(() => {
    return {
      total: cvs.length,
      hasPrimary: !!primaryCV,
      totalSize: cvs.reduce((sum, cv) => sum + (cv.fileSize || 0), 0),
      formats: cvs.reduce((acc, cv) => {
        const ext = cv.fileName.split('.').pop().toLowerCase();
        acc[ext] = (acc[ext] || 0) + 1;
        return acc;
      }, {})
    };
  }, [cvs, primaryCV]);

  // Refresh CVs data
  const refresh = useCallback(() => {
    loadCVs();
  }, [loadCVs]);

  // Load CVs on mount and when user changes
  useEffect(() => {
    if (currentUser) {
      loadCVs();
    } else {
      // Clear state when user logs out
      setCvs([]);
      setPrimaryCVState(null);
      setLoading(false);
      setError(null);
    }
  }, [currentUser, loadCVs]);

  return {
    // Data
    cvs,
    primaryCV,
    hasCVs,
    
    // State
    loading,
    uploading,
    error,
    
    // Actions
    uploadCV: handleUploadCV,
    deleteCV: handleDeleteCV,
    setPrimaryCV: handleSetPrimaryCV,
    updateCVMetadata: handleUpdateCVMetadata,
    refresh,
    
    // Utilities
    getCVById,
    isPrimary,
    getStats
  };
};

export default useCV;